import React, { useEffect, useState } from 'react';
import { getDisplayCurrency } from '../utils/displayCurrency';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Shield, CreditCard, Check } from 'lucide-react';
import { apiClient } from '../api/client';

/**
 * /parent-approve-upgrade?token=…
 *
 * Public landing page reached via SMS / email when a teen requests an
 * upgrade. Token IS the auth — no login required.
 *
 *   GET  /api/parent-upgrade/preview/{token}            → teen + plan catalogue
 *   POST /api/parent-upgrade/start-checkout/{token}     → Stripe (parent picks plan)
 *   POST /api/parent-upgrade/decline/{token}            → declined
 *
 * The PARENT picks the plan. The TEEN never sees the prices.
 * On Stripe success, the existing billing webhook activates the TEEN's
 * subscription (metadata.user_id = teen_id, customer_email = parent.email).
 */
const ParentUpgradeApprove = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [preview, setPreview] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This approval link is missing its token.');
      setLoading(false);
      return;
    }
    apiClient
      .request(`/api/parent-upgrade/preview/${encodeURIComponent(token)}`)
      .then((data) => {
        setPreview(data);
        if (data.suggested_plan_id && (data.plans || []).some((p) => p.plan_id === data.suggested_plan_id)) {
          setSelectedPlanId(data.suggested_plan_id);
        } else if ((data.plans || [])[0]) {
          setSelectedPlanId(data.plans[0].plan_id);
        }
      })
      .catch((e) => setError(e?.message || 'This approval link is invalid, used, or expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const formatPrice = (plan) => {
    if (!plan) return '';
    const amt = (plan.plan_price_cents || 0) / 100;
    const symbol = getDisplayCurrency().symbol;
    return `${symbol}${amt.toFixed(2)} / ${plan.plan_interval || 'month'}`;
  };

  const handleApprove = async () => {
    if (!selectedPlanId) return;
    setBusy(true);
    try {
      const res = await apiClient.request(`/api/parent-upgrade/start-checkout/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({ plan_id: selectedPlanId, origin_url: window.location.origin }),
      });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      setError('Could not start checkout. Please try again or contact support.');
    } catch (e) {
      setError(e?.message || 'Could not start checkout.');
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Decline this upgrade request? Your teen will be notified.')) return;
    setBusy(true);
    try {
      await apiClient.request(`/api/parent-upgrade/decline/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setDeclined(true);
    } catch (e) {
      setError(e?.message || 'Could not decline.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-orange-50">
        <Loader2 className="animate-spin text-orange-500" size={36} />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-orange-50 p-6">
        <div className="max-w-md bg-white rounded-3xl shadow-xl p-8 text-center" data-testid="upgrade-link-invalid">
          <XCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link expired or invalid</h1>
          <p className="text-sm text-gray-600">{error || 'This approval link is no longer active. Please ask your teen to request the upgrade again.'}</p>
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-orange-50 p-6">
        <div className="max-w-md bg-white rounded-3xl shadow-xl p-8 text-center" data-testid="upgrade-declined">
          <CheckCircle className="text-emerald-500 mx-auto mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Upgrade declined</h1>
          <p className="text-sm text-gray-600">We&apos;ve let {preview.teen_name} know. No payment has been taken.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-rose-50 py-10 px-4" data-testid="parent-upgrade-approve-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider">
            <Shield size={14} /> Parent approval needed
          </div>
          <h1 className="text-3xl font-black text-gray-900 mt-4">
            {preview.teen_name} wants to upgrade
          </h1>
          <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
            <strong>You pick the plan</strong> below. {preview.teen_name} hasn&apos;t seen any prices and won&apos;t be charged.
            Nothing is taken until you tap <strong>Approve & Pay</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="parent-plan-picker">
          {(preview.plans || []).map((plan) => {
            const isSelected = selectedPlanId === plan.plan_id;
            return (
              <button
                key={plan.plan_id}
                onClick={() => setSelectedPlanId(plan.plan_id)}
                className={`text-left rounded-3xl p-5 sm:p-6 transition shadow-sm hover:shadow-md ${
                  isSelected
                    ? 'bg-white border-2 ring-2 ring-orange-300'
                    : 'bg-white/85 border-2 border-gray-200 hover:border-gray-300'
                }`}
                style={isSelected ? { borderColor: '#F5831F' } : {}}
                data-testid={`parent-plan-${plan.plan_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Plan</p>
                    <p className="text-lg font-extrabold text-gray-900">{plan.plan_name}</p>
                  </div>
                  {isSelected ? (
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <p className="text-2xl font-black text-gray-900" data-testid={`parent-plan-${plan.plan_id}-price`}>
                  {formatPrice(plan)}
                </p>
                {plan.features && plan.features.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {plan.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <Check size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                )}
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-5 mb-6" data-testid="parent-billing-summary">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="text-blue-500" size={18} />
            <p className="text-sm font-bold text-gray-900">Billing summary</p>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Beneficiary</span>
              <span className="font-semibold text-gray-900">{preview.teen_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Teens covered</span>
              <span className="font-semibold text-gray-900">{preview.teen_count || 1}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Selected plan</span>
              <span className="font-semibold text-gray-900">
                {(preview.plans.find((p) => p.plan_id === selectedPlanId) || {}).plan_name || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Billed to</span>
              <span className="font-semibold text-gray-900">You (your card)</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleApprove}
            disabled={busy || !selectedPlanId}
            data-testid="upgrade-approve-pay-btn"
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
            Approve & Pay
          </button>
          <button
            onClick={handleDecline}
            disabled={busy}
            data-testid="upgrade-decline-btn"
            className="w-full py-3 border border-gray-300 text-gray-700 font-semibold rounded-2xl hover:bg-gray-50 transition disabled:opacity-50"
          >
            Decline
          </button>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          This approval link expires in 7 days. If you didn&apos;t expect this, just close the page — nothing happens unless you tap Approve.
        </p>
      </div>
    </div>
  );
};

export default ParentUpgradeApprove;
